import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { productService } from '../services/productService';
import ProductCard from '../components/products/ProductCard';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const featured = await productService.getFeaturedProducts(6);
        setFeaturedProducts(featured);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - 参考Strength Studio TT */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              专业力量
              <span className="block text-orange-500">训练计划</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              探索我们的专业力量训练计划和评价，或使用我们的计划推荐工具找到最适合您的选择！
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/plan"
                className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transition-colors duration-200 border-2 border-orange-600 hover:border-orange-700"
              >
                探索计划
              </Link>
              <Link
                to="/recommend"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 rounded-lg transition-all duration-300"
              >
                计划推荐器
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">训练计划</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              探索我们的专业力量训练计划和评价，或使用我们的计划推荐工具找到最适合您的选择！
            </p>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-300"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-300 rounded mb-3"></div>
                    <div className="h-4 bg-gray-300 rounded mb-2 w-3/4"></div>
                    <div className="h-4 bg-gray-300 rounded mb-4 w-1/2"></div>
                    <div className="flex justify-between items-center">
                      <div className="h-8 bg-gray-300 rounded w-20"></div>
                      <div className="h-10 bg-gray-300 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">暂无训练计划</p>
            </div>
          )}
        </div>
      </section>

      {/* Program Recommender Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">计划选择器</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            我们的计划推荐器是一个用户友好的工具，旨在帮助您找到适合您特定目标和健身水平的理想训练计划。只需回答几个问题，我们就会从我们的专业力量训练选项范围中推荐完美的计划。让您的健身之旅不再有猜测，今天就开始实现您的目标吧！
          </p>
          <Link
            to="/recommend"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-lg transition-colors inline-flex items-center"
          >
            计划推荐器
          </Link>
        </div>
      </section>

      {/* Become a Member Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">成为会员</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            及时了解我们所有的计划发布、网站折扣代码和教练新闻。在下面订阅我们的邮件列表！
          </p>
          <div className="max-w-md mx-auto flex">
            <input
              type="email"
              placeholder="邮箱地址"
              className="flex-1 px-4 py-3 bg-white text-gray-900 rounded-l-lg border-0 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-r-lg font-bold transition-colors">
              订阅
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 